import dayjs from "dayjs";
import { DaylightDateFormat } from "../../constants/DaylightDateFormat";
import type { ServiceFailureReasonCode } from "../../pages/linehaul/ucccOfd/ucccOfdRightPanel/ucccOfdDetails/ucccOfdRightPanelStatusDetails.type";
import type { UcccOfdShipmentInformation } from "../../pages/linehaul/ucccOfd/ucccOfdShipmentInformation.type";
import {
  flattenUcccOfdShipmentsList,
  type UcccOfdDashboardSummaryData,
  type UcccOfdShipmentRow,
  type UcccOfdShipmentsListResponse,
} from "../../pages/linehaul/ucccOfd/ucccOfdShipmentRow.type";

/**
 * Wire-format JSON serialization for UC/CC OFD shipments API.
 * Uses consistent date formatting patterns:
 * - UTC timestamps: ISO_DATE_TIME_WITHOUT_TIMEZONE format
 * - Date-only fields: ISO_DATE format
 */

/** Wire-format JSON for GET uccc-ofd shipments (ISO dates, nullable SIC region ids). */
export const serializeUcccOfdShipmentsListForApi = (
  list: UcccOfdShipmentsListResponse
): Record<string, unknown> => {
  const serializeSic = (sic: UcccOfdShipmentRow["originSic"]) => ({
    id: sic.id,
    code: sic.code,
    ownership: sic.ownership,
    lhRegionId: sic.lhRegionId === 0 ? null : sic.lhRegionId,
    timeZone: sic.timeZone,
  });

  const serializeRow = (row: UcccOfdShipmentRow) => ({
    shipmentId: row.shipmentId,
    proNumber: row.proNumber,
    pickupDateUtc: row.pickupDateUtc
      .utc()
      .format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE),
    dueDate: row.dueDate.format(DaylightDateFormat.ISO_DATE),
    deliveryDateUtc: row.deliveryDateUtc?.isValid()
      ? row.deliveryDateUtc
          .utc()
          .format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : null,
    appointmentStartTimestampUtc: row.appointmentStartTimestampUtc?.isValid()
      ? row.appointmentStartTimestampUtc
          .utc()
          .format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : null,
    originSic: serializeSic(row.originSic),
    destinationSic: serializeSic(row.destinationSic),
    currentSic: row.currentSic ? serializeSic(row.currentSic) : null,
    finalDestination: row.finalDestination,
    pts: row.pts,
    pcs: row.pcs,
    weight: row.weight,
    shipmentTags: row.shipmentTags,
    status: row.status,
    followUpStatus: row.followUpStatus ?? null,
    tripNumber: row.tripNumber ?? null,
    etaUtc: row.etaUtc?.isValid()
      ? row.etaUtc
          .utc()
          .format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : null,
    ataUtc: row.ataUtc?.isValid()
      ? row.ataUtc
          .utc()
          .format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : null,
    etaStatus: row.etaStatus ?? null,
    locationDetails: row.locationDetails ?? null,
    consignee: row.consignee,
    hasProServiceFailure: Boolean(row.hasProServiceFailure),
    isWeatherDelay: Boolean(row.isWeatherDelay),
  });

  return {
    unassignedShipmentCount: list.unassignedShipmentCount,
    unassignedShipments: list.unassignedShipments.map(serializeRow),
    ownerGroups: list.ownerGroups.map((g) => ({
      ownerName: g.ownerName,
      ownerId: g.ownerId,
      shipmentCount: g.shipmentCount,
      shipments: g.shipments.map(serializeRow),
    })),
  };
};

export const mockUcccOfdShipments = {
  default: {
    unassignedShipmentCount: 2,
    unassignedShipments: [
      {
        id: "132916221",
        shipmentId: 132916221,
        proNumber: "P8550057",
        originAgent: null,
        destinationAgent: null,
        pickupDateUtc: dayjs("2024-11-27T20:00:00Z"),
        dueDate: dayjs("2024-12-03"),
        deliveryDateUtc: dayjs("2024-12-03T14:00:00Z"),
        appointmentStartTimestampUtc: null,
        isWeatherDelay: true,
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 4,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 103,
          code: "ATB-SEFL",
          ownership: "V",
          lhRegionId: 0,
          timeZone: "America/New_York",
        },
        currentSic: null,
        finalDestination: "",
        pts: 1,
        pcs: 51,
        weight: 724,
        shipmentTags: ["UC"],
        status: "ATMP",
        followUpStatus: "SERV FAILURE",
        fbFollowUpStatus: "SERV FAILURE",
        isExpedite: true,
        isServiceFailure: true,
        hasProExpedite: true,
        hasProServiceFailure: true,
        expediteCreatedOn: null,
        serviceFailureCreatedOn: dayjs("2024-12-01T10:00:00Z"),
        tripNumber: null,
        etaUtc: null,
        ataUtc: null,
        etaStatus: "OnTime",
        locationDetails: null,
        consignee: {
          id: 126528,
          name: "PIONEER FASTENERS & TOOLS",
          address: "202 S ECTOR DR",
          city: "EULESS",
          state: "TX",
          zipCode: "76040",
        },
      },
      {
        id: "132933608",
        shipmentId: 132933608,
        proNumber: "P8557664",
        originAgent: null,
        destinationAgent: null,
        pickupDateUtc: dayjs("2024-12-02T13:00:00Z"),
        dueDate: dayjs("2024-12-03"),
        deliveryDateUtc: dayjs("2024-12-03T13:00:00Z"),
        appointmentStartTimestampUtc: null,
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 4,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 4,
          timeZone: "America/New_York",
        },
        currentSic: null,
        finalDestination: "",
        pts: 2,
        pcs: 0,
        weight: 1012,
        shipmentTags: ["UC", "LACC", "OVL"],
        status: "ATMP",
        followUpStatus: null,
        fbFollowUpStatus: undefined,
        isExpedite: false,
        isServiceFailure: false,
        isWeatherDelay: false,
        hasProExpedite: false,
        hasProServiceFailure: false,
        expediteCreatedOn: null,
        serviceFailureCreatedOn: null,
        tripNumber: null,
        etaUtc: null,
        ataUtc: null,
        etaStatus: "OnTime",
        locationDetails: null,
        consignee: {
          id: 29559492,
          name: "CENTURY DEVELOPMENT GROUP",
          address: "35-32 LEAVITT ST",
          city: "FLUSHING",
          state: "NY",
          zipCode: "11354",
        },
      },
    ] satisfies UcccOfdShipmentRow[],
    ownerGroups: [
      {
        ownerName: "John Doe",
        ownerId: "bbf600c9-07ac-4ca5-8c35-60724ca9cab8",
        shipmentCount: 2,
        shipments: [
          {
            id: "133001229",
            shipmentId: 133001229,
            proNumber: "P8586143",
            originAgent: null,
            destinationAgent: null,
            pickupDateUtc: dayjs("2024-12-12T21:00:00Z"),
            dueDate: dayjs("2024-12-13"),
            deliveryDateUtc: dayjs("2024-12-16T13:00:00Z"),
            appointmentStartTimestampUtc: null,
            originSic: {
              id: 81,
              code: "MIS",
              ownership: "V",
              lhRegionId: 3,
              timeZone: "America/New_York",
            },
            destinationSic: {
              id: 1,
              code: "EWR",
              ownership: "C",
              lhRegionId: 4,
              timeZone: "America/New_York",
            },
            currentSic: null,
            finalDestination: "",
            pts: 1,
            pcs: 18,
            weight: 360,
            shipmentTags: ["UC"],
            status: "MSPU",
            followUpStatus: null,
            fbFollowUpStatus: undefined,
            isExpedite: false,
            isServiceFailure: false,
            isWeatherDelay: false,
            hasProExpedite: false,
            hasProServiceFailure: false,
            expediteCreatedOn: null,
            serviceFailureCreatedOn: null,
            tripNumber: null,
            etaUtc: null,
            ataUtc: null,
            etaStatus: "OnTime",
            locationDetails: null,
            consignee: {
              id: 309461,
              name: "VINEYARD VINES",
              address: "3 APPAREL COURT",
              city: "MONROE TOWNSHIP",
              state: "NJ",
              zipCode: "08831",
            },
          },
          {
            id: "135247346",
            shipmentId: 135247346,
            proNumber: "162052377",
            originAgent: null,
            destinationAgent: null,
            pickupDateUtc: dayjs("2025-10-27T07:00:00Z"),
            dueDate: dayjs("2025-10-29"),
            deliveryDateUtc: dayjs("2025-10-29T13:00:00Z"),
            appointmentStartTimestampUtc: null,
            originSic: {
              id: 111,
              code: "ONT",
              ownership: "C",
              lhRegionId: 6,
              timeZone: "America/Los_Angeles",
            },
            destinationSic: {
              id: 114,
              code: "DAL",
              ownership: "C",
              lhRegionId: 3,
              timeZone: "America/Chicago",
            },
            currentSic: {
              id: 114,
              code: "DAL",
              ownership: "C",
              lhRegionId: 3,
              timeZone: "America/Chicago",
            },
            finalDestination: "DAL",
            pts: 1,
            pcs: 0,
            weight: 450,
            shipmentTags: ["UC", "LG"],
            status: "DEPT",
            followUpStatus: "EXPEDITE",
            fbFollowUpStatus: "EXPEDITE",
            tripNumber: 3182478,
            etaUtc: dayjs("2025-10-29T11:28:00Z"),
            ataUtc: dayjs("2025-10-29T11:12:43Z"),
            etaStatus: "OnTime",
            locationDetails: null,
            consignee: {
              id: 30550994,
              name: "COMPLETE CUSTOMS",
              address: "421 POWER HOUSE ST",
              city: "MCKINNEY",
              state: "TX",
              zipCode: "75071",
            },
            isExpedite: true,
            isServiceFailure: true,
            isWeatherDelay: true,
            hasProExpedite: true,
            hasProServiceFailure: true,
            expediteCreatedOn: dayjs("2025-10-28T09:00:00Z"),
            serviceFailureCreatedOn: dayjs("2025-10-28T10:00:00Z"),
          },
        ] satisfies UcccOfdShipmentRow[],
      },
      {
        ownerName: "Jane Smith",
        ownerId: "e553a476-dcb6-4cc3-857d-683ea32f7f74",
        shipmentCount: 1,
        shipments: [
          {
            id: "132920428",
            shipmentId: 132920428,
            proNumber: "P8552076",
            originAgent: null,
            destinationAgent: null,
            pickupDateUtc: dayjs("2024-11-25T21:00:00Z"),
            dueDate: dayjs("2026-03-16"),
            deliveryDateUtc: dayjs("2024-12-02T16:00:00Z"),
            appointmentStartTimestampUtc: null,
            originSic: {
              id: 115,
              code: "HTX",
              ownership: "C",
              lhRegionId: 3,
              timeZone: "America/Chicago",
            },
            destinationSic: {
              id: 3,
              code: "SFO",
              ownership: "C",
              lhRegionId: 0,
              timeZone: "America/Los_Angeles",
            },
            currentSic: null,
            finalDestination: "",
            pts: 1,
            pcs: 3,
            weight: 260,
            shipmentTags: ["UC"],
            status: "ATMP",
            followUpStatus: null,
            fbFollowUpStatus: undefined,
            isExpedite: false,
            isServiceFailure: false,
            isWeatherDelay: false,
            hasProExpedite: false,
            hasProServiceFailure: false,
            expediteCreatedOn: null,
            serviceFailureCreatedOn: null,
            tripNumber: null,
            etaUtc: null,
            ataUtc: null,
            etaStatus: "OnTime",
            locationDetails: null,
            consignee: {
              id: 29552935,
              name: "INCHCAPE SHIPPING SERVICES",
              address: "3065 RICHMOND PARKWAY SUITE 108A",
              city: "RICHMOND",
              state: "CA",
              zipCode: "94806",
            },
          },
        ] satisfies UcccOfdShipmentRow[],
      },
    ],
  },
} satisfies Record<string, UcccOfdShipmentsListResponse>;

/** All rows across owner groups and unassigned (for Storybook/tests default table rows). */
export const mockUcccOfdShipmentsFlatRows = flattenUcccOfdShipmentsList(
  mockUcccOfdShipments.default
);

/** Dashboard-summary API payload (MSW + tests). */
export const mockUcccOfdDashboardSummary = {
  default: {
    totalUcCcShipments: 23,
    serviceFailureCount: 19,
    deliveredCount: 3,
    onTimePerformance: {
      percentage: 97.4,
      onTimeCount: 22,
      total: 23,
    },
    followUpStatuses: {
      delivered: 3,
      expedite: 4,
      ofd: 3,
      osd: 0,
      pendingAppt: 3,
      pendingPod: 2,
      serviceFailure: 0,
      noStatus: 5,
    },
  },
} as const satisfies Record<string, UcccOfdDashboardSummaryData>;

export type DimItem = {
  lengthIn: number;
  widthIn: number;
  heightIn: number;
};

type UcccOfdShipmentDetailsMock = {
  shipment: UcccOfdShipmentInformation;
  dimensions: DimItem[];
};

const mockUcccOfdShipmentDetailsDimensions: DimItem[] = [
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 22, widthIn: 36, heightIn: 50 },
  { lengthIn: 39, widthIn: 42, heightIn: 47 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 39, widthIn: 42, heightIn: 50 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
];

export const mockUcccOfdShipmentDetails = {
  default: {
    shipment: {
      proNumber: "180403917",
      originAgent: null,
      destinationAgent: null,
      status: "ENRT",
      statusStartDateUtc: dayjs("2026-02-02T08:19:10.211Z").toDate(),
      shipmentTags: ["UC", "CC", "RESI", "APTM"],
      originSic: {
        code: "CHT",
        zipCode: "28206",
        timeZone: "America/New_York",
      },
      destinationSic: {
        code: "ONT",
        zipCode: "92337",
        timeZone: "America/Los_Angeles",
      },
      serviceDue: dayjs("2026-02-06T00:00:00").toDate(),
      appointmentStartTimeUtc: null,
      appointmentEndTimeUtc: null,
      pallets: 1,
      pieces: 0,
      weight: 287,
      shippingInfo: {
        shipper: {
          name: "SPROGS",
          address: "716 N SECREST AVE",
          city: "MONROE",
          state: "NC",
          zipCode: "28110",
          phoneNumber: "",
        },
        consignee: {
          name: "MARGUERITTE MARSHALL",
          address: "80455 CAMINO SAN LUCAS",
          city: "INDIO",
          state: "CA",
          zipCode: "92203",
          phoneNumber: "425 985 4088",
        },
      },
      descriptionOfArticles: {
        items: [
          {
            commodityCode: "79300-3",
            description: "LOCKERS CL250",
            rateClass: "125",
            actualClass: "250",
          },
        ],
        instructions: [
          "Do Not Break Down Pallet",
          "Do Not Double Stack",
          "Fragile - Handle With Care",
        ],
      },
      palletDimensions: [
        { refDimId: 1, length: 48, width: 40, height: 60 },
        { refDimId: 2, length: 50, width: 42, height: 55 },
        { refDimId: 3, length: 45, width: 38, height: 58 },
        { refDimId: 4, length: 52, width: 44, height: 62 },
        { refDimId: 5, length: 47, width: 39, height: 57 },
      ],
      fbFollowUpStatusDetails: {
        isExpedite: true,
        isServiceFailure: true,
        isWeatherDelay: true,
        trackingDetails: {
          expedite: {
            note: "Test-02",
            amount: 30,
            carrierCode: "L004",
            carrierName: "COVENANT TRANSPORT INC",
            employeeCode: null,
            employeeName: null,
            expediteCreatedOn: dayjs("2026-03-25T11:17:31.5775086Z"),
            expediteCreatedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            expediteCreatorName: "Praveen Donga",
            expediteModifiedOn: dayjs("2026-03-25T11:25:16.0564507Z"),
            expediteModifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            expediteModifierName: "Praveen Donga",
          },
          serviceFailure: {
            failureReasonCode: "AGENT FAILURE",
            subReason: "LATE OB CLOSE",
            explanation: "[Agent] [Origin] terminal overflowed the shipment",
            serviceFailureCreatedOn: dayjs("2026-03-18T15:30:00.6868273Z"),
            serviceFailureCreatedBy: "13a04900-fb21-41c5-b9cc-65b083a53c62",
            serviceFailureCreatorName: "Keerthi Majji",
            serviceFailureModifiedOn: dayjs("2026-03-23T18:19:25.8149345Z"),
            serviceFailureModifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            serviceFailureModifierName: "Praveen Donga",
          },
        },
        fbFollowUpStatus: "EXPEDITE",
        createdOn: dayjs("2026-03-18T12:08:37.515281Z"),
        createdBy: "c4cf2858-b7f2-44ed-b253-2e0bf6f8b9bd",
        modifiedOn: dayjs("2026-03-25T11:25:16.05645Z"),
        modifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
      },
    },
    dimensions: mockUcccOfdShipmentDetailsDimensions,
  },
} satisfies Record<string, UcccOfdShipmentDetailsMock>;

export const mockServiceFailureReasonCodes: ServiceFailureReasonCode[] = [
  {
    id: 1,
    failureReasonCode: "AGENT FAILURE",
    subReasons: [
      {
        id: 1,
        subReason: "LATE OB CLOSE",
        explanation: "AGENT CUT THE LOAD OUT LATE",
      },
      {
        id: 2,
        subReason: "OVERFLOW",
        explanation: "ORIGIN TERMINAL OVERFLOWED THE SHIPMENT",
      },
      {
        id: 3,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 4,
        subReason: "CAPACITY",
        explanation:
          "DELAYED DUE TO [Agent] ERROR (LACK OF MANPOWER, FREIGHT DELIVERED TO WRONG CONSIGNEE, ETC.)",
      },
      {
        id: 5,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (AFTER 5PM FOR UC OR AFTER SPECIFIED CC WINDOW)",
      },
      {
        id: 6,
        subReason: "MISSED ORIGIN HUB TRANSFER",
        explanation:
          "[Origin] SERVICE CENTER FAILED TO TRANSFER SHIPMENT TO OB LOADING HUB",
      },
      {
        id: 7,
        subReason: "DEST HELD",
        explanation: "[Destination] TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 8,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 9,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 2,
    failureReasonCode: "DAYLIGHT FAILURE",
    subReasons: [
      {
        id: 10,
        subReason: "LATE OB CLOSE",
        explanation: "[Origin] SERVICE CENTER CUT THE LOAD OUT LATE",
      },
      {
        id: 11,
        subReason: "OVERFLOW",
        explanation: "[Origin] SERVICE CENTER OVERFLOWED THE SHIPMENT",
      },
      {
        id: 12,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 13,
        subReason: "DEST HELD",
        explanation: "DESTINATION TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 14,
        subReason: "DRIVER PERFORMANCE",
        explanation: "DELAYED DUE TO DAYLIGHT DRIVER PERFORMANCE RELATED ISSUE",
      },
      {
        id: 15,
        subReason: "MECH",
        explanation:
          "DRIVER HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 16,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (MISSED GUARANTEED DELIVERY WINDOW, CC DELIVERY BEFORE 9 AM, ETC.)",
      },
      {
        id: 17,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 18,
        subReason: "SVC STANDARD REVIEW",
        explanation: "DELIVERY UNDER REVIEW WITH DIRECTOR",
      },
      {
        id: 19,
        subReason: "SERVICE CENTER ERROR",
        explanation: "SERVICE CENTER RELATED ERROR",
      },
      {
        id: 20,
        subReason: "LINEHAUL ERROR",
        explanation: "LINEHAUL TEAM RELATED ERROR",
      },
      {
        id: 21,
        subReason: "BILLING ERROR",
        explanation:
          "NOT BILLED IN TIME OR BILLED TOO LATE CAUSING LOSS OF VISIBILITY",
      },
      {
        id: 22,
        subReason: "CS ERROR",
        explanation: "CLIENT SERVICES TEAM RELATED ERROR",
      },
      {
        id: 23,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 3,
    failureReasonCode: "CARRIER FAILURE",
    subReasons: [
      {
        id: 24,
        subReason: "LATE PICKUP",
        explanation: "[Carrier] PICKED UP LOAD LATE FROM [Origin]",
      },
      {
        id: 25,
        subReason: "MECH",
        explanation:
          "[Carrier] HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 26,
        subReason: "DRIVER PERFORMANCE",
        explanation:
          "DELAYED DUE TO DRIVER PERFORMANCE RELATED ISSUE (FATIGUE, ILLNESS, DRIVER GOT OFF TRUCK)",
      },
      {
        id: 27,
        subReason: "CARRIER PERFORMANCE",
        explanation:
          "DELAYED DUE TO [Carrier] RELATED ISSUE (LATE REPOWER, DRIVER HOME TIME, BOOKED LOAD WITH WRONG DELIVERY INFO, ETC.)",
      },
      {
        id: 28,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
];
